import { useEffect, useMemo, useRef, useState } from 'react';

import { styled, useForkRef } from '@mui/material';

import { useDatePicker } from '../hooks/useDatePicker';
import { useDatePickerInput } from '../hooks/useDatePickerInput';
import { useDatePickerLocale } from '../hooks/useDatePickerLocale';
import { datePickerClasses } from '../theme';
import { isRangePicker } from '../utils';
import DatePickerCalendar from './DatePickerCalendar';
import DatePickerInput from './DatePickerInput';
import { PickerConfigProvider } from './PickerConfigContext';

import type {
  DatePickerCustomInputRenderProps,
  DatePickerProps,
  PeriodPickerProps,
} from '../types';
import type { PickerConfig } from './PickerConfig';

interface PickerShellProps {
  props: DatePickerProps | PeriodPickerProps;
  pickerConfig?: PickerConfig;
}

/**
 * input, Popper 열림 상태와 custom input을 공유하는 shell 컴포넌트
 * 기존 DatePicker의 root DOM 및 DatePickerInput 전달 방식은 그대로 유지
 */
export default function PickerShell({ props, pickerConfig }: PickerShellProps) {
  const {
    customInput,
    inputProps,
    placeholderText,
    disabled,
    error,
    onOpen,
    onClose,
    className,
    inline = false,
    name,
    ref: externalRef,
  } = props;

  const locale = useDatePickerLocale(props.locale);
  const internalRef = useRef<HTMLDivElement>(null);
  const mergedRef = useForkRef(internalRef, externalRef);
  const [isOpen, setIsOpen] = useState(inline);
  const [inputValue, setInputValue] = useState('');

  const { dateFormat: computedDateFormat, mask } = useDatePicker({ props, pickerConfig });

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const { handleInputChange, getFormattedValue } = useDatePickerInput({
    dateFormat: computedDateFormat,
    mask,
    onClose: handleClose,
    pickerConfig,
    props,
  });

  const isRangeMode = isRangePicker(props);
  const selectsStart = 'selectsStart' in props ? props.selectsStart : undefined;
  const selectsEnd = 'selectsEnd' in props ? props.selectsEnd : undefined;
  const hasRangeContext = isRangeMode || !!selectsStart || !!selectsEnd;
  const extractedProps = useMemo(
    () => ({
      endDate: hasRangeContext ? props.endDate : undefined,
      excludeDates: props.excludeDates,
      maxDate: props.maxDate,
      minDate: props.minDate,
      monthsShown: 'monthsShown' in props ? props.monthsShown : undefined,
      onChange: props.onChange,
      selected: !isRangeMode ? props.selected : undefined,
      selectsEnd,
      selectsRange: isRangeMode,
      selectsStart,
      shouldCloseOnSelect: props.shouldCloseOnSelect,
      showSeconds: 'showSeconds' in props ? props.showSeconds : undefined,
      showTime: 'showTime' in props ? props.showTime : undefined,
      showTimezone: 'showTimezone' in props ? props.showTimezone : undefined,
      showToday: 'showToday' in props ? props.showToday : undefined,
      startDate: hasRangeContext ? props.startDate : undefined,
      startOfWeek: 'startOfWeek' in props ? props.startOfWeek : undefined,
      minuteInterval: 'minuteInterval' in props ? props.minuteInterval : undefined,
      hourInterval: 'hourInterval' in props ? props.hourInterval : undefined,
      secondInterval: 'secondInterval' in props ? props.secondInterval : undefined,
    }),
    [hasRangeContext, isRangeMode, props, selectsEnd, selectsStart],
  );

  const handleOpen = () => {
    if (disabled || inline) return;
    setIsOpen(true);
    onOpen?.();
  };

  const handleInputFocus = () => {
    if (inline) return;
    handleOpen();
  };

  const handleInputChangeWithState = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string,
  ) => {
    const currentInputValue = newValue || event.target.value;
    const normalized = handleInputChange(event, newValue);
    setInputValue(normalized ?? currentInputValue);
  };

  const formattedValue = getFormattedValue();
  const calendarConfig = pickerConfig ? { ...pickerConfig, disabled } : undefined;
  const inputName = name ?? inputProps?.name;

  useEffect(() => {
    setInputValue(formattedValue);
  }, [formattedValue]);

  const renderInput = () => {
    if (customInput) {
      const renderProps: DatePickerCustomInputRenderProps = {
        close: handleClose,
        disabled,
        error,
        isOpen,
        name,
        open: handleOpen,
        ref: mergedRef,
        value: inputValue || getFormattedValue(),
      };
      return <div ref={mergedRef}>{customInput(renderProps)}</div>;
    }

    const {
      value: _inputPropsValue,
      onFocus: _onFocus,
      onChange: _onChange,
      ...restInputProps
    } = inputProps || {};

    return (
      <DatePickerInput
        ref={mergedRef}
        onChange={handleInputChangeWithState}
        onFocus={handleInputFocus}
        value={inputValue}
        error={error}
        disabled={disabled}
        placeholder={placeholderText}
        showTimezone={inline ? false : extractedProps.showTimezone}
        mask={mask}
        {...restInputProps}
        name={inputName}
      />
    );
  };

  const calendar = (
    <DatePickerCalendar
      anchorEl={internalRef.current}
      dateFormat={computedDateFormat}
      endDate={extractedProps.endDate}
      excludeDates={extractedProps.excludeDates}
      inline={inline}
      locale={locale}
      maxDate={extractedProps.maxDate}
      minDate={extractedProps.minDate}
      monthsShown={extractedProps.monthsShown}
      name={name}
      onClose={handleClose}
      onChange={extractedProps.onChange}
      open={isOpen}
      selected={extractedProps.selected}
      selectsEnd={extractedProps.selectsEnd}
      selectsRange={extractedProps.selectsRange}
      selectsStart={extractedProps.selectsStart}
      shouldCloseOnSelect={extractedProps.shouldCloseOnSelect}
      showSeconds={extractedProps.showSeconds}
      showTime={extractedProps.showTime}
      showTimezone={extractedProps.showTimezone}
      showToday={extractedProps.showToday}
      startDate={extractedProps.startDate}
      startOfWeek={extractedProps.startOfWeek}
      minuteInterval={extractedProps.minuteInterval}
      hourInterval={extractedProps.hourInterval}
      secondInterval={extractedProps.secondInterval}
    />
  );

  // calendarConfig 유무로 PickerConfigProvider 래핑 여부 결정
  return (
    <DatePickerRoot className={className}>
      {renderInput()}
      {calendarConfig ? (
        <PickerConfigProvider pickerConfig={calendarConfig}>{calendar}</PickerConfigProvider>
      ) : (
        calendar
      )}
    </DatePickerRoot>
  );
}

const DatePickerRoot = styled('div', {
  target: datePickerClasses.root,
})``;
